import {
  AnchorTreeLeafNode,
  AnchorTreeNode
} from '../types/anchor-tree';

/** Depth-first walk; visitor returning true stops the walk. */
function walk(root: AnchorTreeNode | null, visitor: (node: AnchorTreeNode) => boolean): void {
  if (!root) {
    return;
  }
  const stack: AnchorTreeNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (visitor(node)) {
      return;
    }
    if (node.kind === 'decision') {
      for (const branch of node.branches ?? []) {
        stack.push(branch.child);
      }
    }
  }
}

/** Returns the root→leaf node path (inclusive) or null when leaf not found. */
function findPathToLeaf(root: AnchorTreeNode | null, leafNodeId: string): AnchorTreeNode[] | null {
  if (!root) {
    return null;
  }
  const dfs = (node: AnchorTreeNode, path: AnchorTreeNode[]): AnchorTreeNode[] | null => {
    const nextPath = [...path, node];
    if (node.kind === 'leaf') {
      return node.id === leafNodeId ? nextPath : null;
    }
    for (const branch of node.branches ?? []) {
      const result = dfs(branch.child, nextPath);
      if (result) {
        return result;
      }
    }
    return null;
  };
  return dfs(root, []);
}

/** Finds a leaf node by id. */
export function findLeaf(root: AnchorTreeNode | null, leafNodeId: string): AnchorTreeLeafNode | null {
  let found: AnchorTreeLeafNode | null = null;
  walk(root, node => {
    if (node.kind === 'leaf' && node.id === leafNodeId) {
      found = node;
      return true;
    }
    return false;
  });
  return found;
}

/** Finds the leaf a template is placed on (tree templateIds = source of truth). */
export function findLeafForTemplate(
  root: AnchorTreeNode | null,
  templateId: string
): AnchorTreeLeafNode | null {
  let found: AnchorTreeLeafNode | null = null;
  walk(root, node => {
    if (node.kind === 'leaf' && (node.templateIds ?? []).includes(templateId)) {
      found = node;
      return true;
    }
    return false;
  });
  return found;
}

/** Collects all leaves of the tree (document order). */
export function collectLeaves(root: AnchorTreeNode | null): AnchorTreeLeafNode[] {
  const leaves: AnchorTreeLeafNode[] = [];
  const visit = (node: AnchorTreeNode): void => {
    if (node.kind === 'leaf') {
      leaves.push(node);
      return;
    }
    for (const branch of node.branches ?? []) {
      visit(branch.child);
    }
  };
  if (root) {
    visit(root);
  }
  return leaves;
}

/** Collects all template ids placed anywhere on the tree. */
export function collectPlacedTemplateIds(root: AnchorTreeNode | null): Set<string> {
  const ids = new Set<string>();
  for (const leaf of collectLeaves(root)) {
    for (const id of leaf.templateIds ?? []) {
      ids.add(id);
    }
  }
  return ids;
}

/**
 * Returns the decisiveAnchorFieldId of every decision node on the root→leaf
 * path, in order. Empty array when the leaf is not found.
 */
export function getPathDecisiveFieldIds(root: AnchorTreeNode | null, leafNodeId: string): string[] {
  const path = findPathToLeaf(root, leafNodeId);
  if (!path) {
    return [];
  }
  return path
    .filter((n): n is Extract<AnchorTreeNode, { kind: 'decision' }> => n.kind === 'decision')
    .map(n => n.decisiveAnchorFieldId);
}

/**
 * The branch expectations along the root→leaf path: for each decision node on
 * the path, its decisive field id plus the branch expected value that was
 * taken to continue toward the leaf. Used for Q8 placement warnings.
 */
export function getPathBranchExpectations(
  root: AnchorTreeNode | null,
  leafNodeId: string
): { fieldId: string; expectedValue: string }[] {
  const path = findPathToLeaf(root, leafNodeId);
  if (!path) {
    return [];
  }
  const expectations: { fieldId: string; expectedValue: string }[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const node = path[i];
    if (node.kind !== 'decision') {
      continue;
    }
    const next = path[i + 1];
    const branch = (node.branches ?? []).find(b => b.child === next || b.child.id === next.id);
    if (branch) {
      expectations.push({ fieldId: node.decisiveAnchorFieldId, expectedValue: branch.expectedValue });
    }
  }
  return expectations;
}

/**
 * The "expected set" for a leaf: path decisive anchors ∪ leafAnchorFieldIds
 * (deduped, path first). Used by scoped extraction and mismatch warnings.
 */
export function getExpectedFieldIdsForLeaf(root: AnchorTreeNode | null, leafNodeId: string): string[] {
  const leaf = findLeaf(root, leafNodeId);
  if (!leaf) {
    return [];
  }
  const pathIds = getPathDecisiveFieldIds(root, leafNodeId);
  return [...new Set([...pathIds, ...(leaf.leafAnchorFieldIds ?? [])])];
}

